import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import {
  GoogleMap,
  Polygon,
  InfoWindow,
} from "@react-google-maps/api";

import { Button } from "antd";

import ElectionModeDashboard from "./ElectionModeDashboard";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import useAxiosInstance from "../../lib/useAxiosInstance";
import { mapStyles } from "../../utils/mapStyles";

const ElectionModeMapView = ({
  isLoaded,
  open,
  setIsFullScreen,
  id,
  center,
  polygonPaths = [],
  mapRef,
  electionMode,
  showCloseButton = true,
  leftPanelOpen,
  setLeftPanelOpen,
  filters,
  setFilters,
  onFilterOptionsReady,
}) => {
  const { t } = useTranslation();
  const internalRef = useRef(null);
  const axiosInstance = useAxiosInstance();
  const mapRefToUse = mapRef || internalRef;

  const [selectedPolygon, setSelectedPolygon] = useState(null);
  const [selectedLocationName, setSelectedLocationName] = useState(null);
  const [mapAction, setMapAction] = useState(null);
  const [hoveredPolygon, setHoveredPolygon] = useState(null);
  const initialViewRef = useRef(null);

  // Holds the clear function provided by ElectionModeDashboard via onClearReady
  const [clearDashboardFilters, setClearDashboardFilters] = useState(null);

  const themes = localStorage.getItem("themes");
  const [dashboardMapState, setDashboardMapState] = useState({
    region: "",
    district: "",
    constituency: "",
    selectedConstituencies: [],
  });

  const mapStyle = useMemo(() => {
    if (!(themes && themes === "darkMode")) return undefined;
    return [
      {
        elementType: "geometry",
        stylers: [{ color: "#1a2332" }],
      },
      {
        elementType: "labels.text.fill",
        stylers: [{ color: "#ffffff" }],
      },
      {
        elementType: "labels.text.stroke",
        stylers: [{ color: "#1a2332" }],
      },
      {
        featureType: "administrative",
        elementType: "geometry.stroke",
        stylers: [{ color: "#4a6fa5" }, { lightness: -10 }],
      },
      {
        featureType: "administrative.land_parcel",
        elementType: "labels.text.fill",
        stylers: [{ color: "#64779f" }],
      },
      {
        featureType: "administrative.province",
        elementType: "geometry.stroke",
        stylers: [{ color: "#4a6fa5" }],
      },
      {
        featureType: "administrative.locality",
        elementType: "labels",
        stylers: [{ visibility: "off" }],
      },

      // Hide neighborhood names
      {
        featureType: "administrative.neighborhood",
        elementType: "labels",
        stylers: [{ visibility: "off" }],
      },
      {
        featureType: "landscape",
        elementType: "geometry",
        stylers: [{ color: "#263238" }],
      },
      {
        featureType: "landscape.man_made",
        elementType: "geometry.stroke",
        stylers: [{ color: "#334e87" }],
      },
      {
        featureType: "landscape.natural",
        elementType: "geometry",
        stylers: [{ color: "#263238" }],
      },
      {
        featureType: "road",
        stylers: [{ visibility: "off" }],
      },
      {
        featureType: "road",
        elementType: "labels.icon",
        stylers: [{ visibility: "off" }],
      },
      {
        featureType: "poi",
        elementType: "labels",
        stylers: [{ visibility: "off" }],
      },
      {
        featureType: "water",
        elementType: "geometry",
        stylers: [{ color: "#0e1626" }],
      },
      {
        featureType: "water",
        elementType: "labels.text.fill",
        stylers: [{ color: "#4e6d70" }],
      },
    ];
  }, [themes]);
  
  useEffect(() => {
    initialViewRef.current = null;
  }, [id]);

  const mapOptions = useMemo(
    () => ({
      styles: themes === "darkMode" ? mapStyle : mapStyles,
      disableDefaultUI: true,
      fullscreenControl: false,
      gestureHandling: "greedy",
      clickableIcons: false,
    }),
    [themes, mapStyle]
  );

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [open]);

  const { data: polygonData } = useQuery(
    [
      "surveyColors",
      id,
      dashboardMapState.region,
      dashboardMapState.district,
      dashboardMapState.constituency,
    ],
    async () => {
      const res = await axiosInstance.get(
        `/api/electionMode/polygonColor/${id}`,
        {
          params: {
            region: dashboardMapState.region || undefined,
            district: dashboardMapState.district || undefined,
            constituency: dashboardMapState.constituency || undefined,
          },
        }
      );
      return res.data?.data || [];
    },
    {
      enabled: !!id,
      refetchOnWindowFocus: false,
    }
  );

  // ─── Smooth zoom helpers ─────────────────────────────────────────────────────

  // Tracks the in-flight smooth-zoom animation so we can cancel it if a new
  // navigation happens before the previous one finishes.
  const zoomAnimationRef = useRef(null);

  // Compute the zoom level that fits the given bounds inside the map viewport.
  const getBoundsZoomLevel = useCallback((bounds, mapDim) => {
    const WORLD_DIM = { height: 256, width: 256 };
    const ZOOM_MAX = 21;

    const latRad = (lat) => {
      const sin = Math.sin((lat * Math.PI) / 180);
      const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
      return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
    };
    const zoom = (mapPx, worldPx, fraction) =>
      Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    const latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI;
    const lngDiff = ne.lng() - sw.lng();
    const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;

    const latZoom = zoom(mapDim.height, WORLD_DIM.height, latFraction || 1e-9);
    const lngZoom = zoom(mapDim.width, WORLD_DIM.width, lngFraction || 1e-9);

    return Math.min(latZoom, lngZoom, ZOOM_MAX);
  }, []);

  // Smoothly pans + zooms the map to fit the given bounds, stepping the zoom one
  // level at a time so the transition feels animated instead of an instant jump.
  const smoothFitBounds = useCallback(
    (mapInstance, bounds) => {
      if (!window.google || !mapInstance || !bounds || bounds.isEmpty()) return;

      // Cancel any animation already running.
      if (zoomAnimationRef.current) {
        clearTimeout(zoomAnimationRef.current);
        zoomAnimationRef.current = null;
      }

      const div = mapInstance.getDiv();
      const mapDim = {
        width: div?.offsetWidth || 800,
        height: div?.offsetHeight || 600,
      };

      const targetCenter = bounds.getCenter();
      const rawZoom = getBoundsZoomLevel(bounds, mapDim);

      // If we can't compute a sane target zoom, or the map isn't ready to report
      // its current zoom, fall back to the instant (but reliable) fitBounds.
      const currentZoom = mapInstance.getZoom();
      if (!Number.isFinite(rawZoom) || !Number.isFinite(currentZoom)) {
        mapInstance.fitBounds(bounds);
        return;
      }

      // Subtract one level for a little breathing room (acts like padding).
      const targetZoom = Math.max(0, Math.min(rawZoom - 1, 21));

      // Smooth (animated) pan to the new center.
      mapInstance.panTo(targetCenter);

      const stepZoom = () => {
        const current = mapInstance.getZoom();
        if (!Number.isFinite(current) || current === targetZoom) {
          zoomAnimationRef.current = null;
          return;
        }
        const next = current < targetZoom ? current + 1 : current - 1;
        mapInstance.setZoom(next);
        zoomAnimationRef.current = setTimeout(stepZoom, 80);
      };

      stepZoom();
    },
    [getBoundsZoomLevel]
  );

  // Cancel any pending zoom animation on unmount.
  useEffect(() => {
    return () => {
      if (zoomAnimationRef.current) clearTimeout(zoomAnimationRef.current);
    };
  }, []);

  // ─── Helpers ────────────────────────────────────────────────────────────────

  const fitToAllPolygons = (mapInstance) => {
    if (!window.google || !mapInstance || !polygonPaths?.length) {
      if (center) {
        mapInstance?.setCenter(center);
        mapInstance?.setZoom(10);
      }
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    polygonPaths.forEach((polygon) => {
      polygon.coordinate?.forEach((point) => {
        const lat = Number(point?.lat);
        const lng = Number(point?.lng);
        if (!isNaN(lat) && !isNaN(lng)) bounds.extend({ lat, lng });
      });
    });

    if (!bounds.isEmpty()) {
      smoothFitBounds(mapInstance, bounds);
    } else if (center) {
      mapInstance.setCenter(center);
      mapInstance.setZoom(10);
    }
  };

  const fitToPolygons = (mapInstance, polygons, fallbackCenter) => {
    if (!window.google || !mapInstance || !polygons || polygons.length === 0) {
      if (fallbackCenter) {
        mapInstance?.setCenter(fallbackCenter);
        mapInstance?.setZoom(10);
      }
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    polygons.forEach((polygon) => {
      polygon.coordinate?.forEach((point) => {
        const lat = Number(point?.lat);
        const lng = Number(point?.lng);
        if (!isNaN(lat) && !isNaN(lng)) bounds.extend({ lat, lng });
      });
    });

    if (!bounds.isEmpty()) {
      smoothFitBounds(mapInstance, bounds);
    } else if (fallbackCenter) {
      mapInstance.setCenter(fallbackCenter);
      mapInstance.setZoom(10);
    }
  };

  useEffect(() => {
    if (!isLoaded || !mapRefToUse.current) return;
    // const timer = setTimeout(() => {
    //   fitToPolygons(mapRefToUse.current, polygonPaths, center);
    // }, 100);
    // return () => clearTimeout(timer);
  }, [polygonPaths, isLoaded, center]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedLocationName || !polygonPaths?.length) {
      if (!selectedLocationName) setSelectedPolygon(null);
      return;
    }
    const match = polygonPaths.find(
      (polygon) =>
        polygon.name?.toString().trim().toLowerCase() ===
        selectedLocationName.toString().trim().toLowerCase()
    );
    if (match) handlePolygonClick(match);
  }, [selectedLocationName, polygonPaths]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const areaSelected = dashboardMapState.district || dashboardMapState.region;
    if (
      !areaSelected ||
      dashboardMapState.constituency ||
      !dashboardMapState.selectedConstituencies?.length ||
      !window.google ||
      !mapRefToUse.current
    ) {
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    polygonPaths
      .filter((polygon) => isPolygonHighlighted(polygon.name))
      .forEach((polygon) => {
        polygon.coordinate?.forEach((point) => bounds.extend(point));
      });

    if (!bounds.isEmpty()) {
      smoothFitBounds(mapRefToUse.current, bounds);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dashboardMapState.region,
    dashboardMapState.district,
    dashboardMapState.constituency,
    dashboardMapState.selectedConstituencies,
    polygonPaths,
  ]);

  const handlePolygonLoad = (polygonInstance) => {
    polygonInstance.setMap(mapRefToUse.current);
  };

  const handlePolygonClick = (polygon) => {
    setSelectedPolygon(polygon);
    setSelectedLocationName(polygon.name);

    if (!window.google || !mapRefToUse.current) return;

    const bounds = new window.google.maps.LatLngBounds();
    polygon.coordinate?.forEach((p) => {
      bounds.extend({
        lat: Number(p.lat),
        lng: Number(p.lng),
      });
    });

    if (!bounds.isEmpty()) {
      smoothFitBounds(mapRefToUse.current, bounds);
    }
  };

  /**
   * Called when the constituency InfoWindow is closed (Back button or X).
   * Only closes the InfoWindow (deselects the polygon) and zooms out — the
   * region/district/constituency filters are left as-is so the user can keep
   * navigating within the currently selected area.
   */
 const handleBackClick = () => {
  setSelectedPolygon(null);
  setSelectedLocationName(null);
      console.log("dashboardMapState",dashboardMapState)
  clearDashboardFilters?.();
  restoreInitialView();
};

  /**
   * Called when the district/region InfoWindow is closed.
   * Only zooms out — filters are left untouched so the user can keep
   * navigating within the currently selected block/region/district.
   */
   const handleAreaInfoClose = () => {
    console.log("dashboardMapState",dashboardMapState)
    restoreInitialView();
  };

  // ─── Polygon color / meta helpers ───────────────────────────────────────────

  const normalize = (value) => value?.toString().trim().toLowerCase();

  // Build a name → meta lookup once per polygonData change, so each polygon does
  // an O(1) lookup instead of scanning the whole array on every render.
  const polygonMetaMap = useMemo(() => {
    const map = new Map();
    (polygonData || []).forEach((item) => {
      map.set(normalize(item.constituency), item);
    });
    return map;
  }, [polygonData]);

  const getPolygonColor = (name) =>
    polygonMetaMap.get(normalize(name))?.color || "transparent";

  const getPolygonMeta = (name) => polygonMetaMap.get(normalize(name)) || {};

  const formatPercent = (value) => {
    const number = Number(value) || 0;
    const percent = Math.abs(number) <= 1 ? number * 100 : number;
    return `${percent.toFixed(1)}%`;
  };

  // Memoized set of highlighted constituency names for O(1) lookups.
  const highlightedSet = useMemo(() => {
    const set = new Set();
    (dashboardMapState.selectedConstituencies || []).forEach((item) =>
      set.add(normalize(item))
    );
    return set;
  }, [dashboardMapState.selectedConstituencies]);

  const isPolygonHighlighted = (name) => highlightedSet.has(normalize(name));

  const getAreaInfoPosition = () => {
    const areaSelected = dashboardMapState.district || dashboardMapState.region;
    if (
      !areaSelected ||
      !dashboardMapState.selectedConstituencies?.length ||
      !window.google
    ) {
      return null;
    }

    const bounds = new window.google.maps.LatLngBounds();
    polygonPaths
      .filter((polygon) => isPolygonHighlighted(polygon.name))
      .forEach((polygon) => {
        polygon.coordinate?.forEach((point) => bounds.extend(point));
      });

    return bounds.isEmpty() ? null : bounds.getCenter();
  };

  // ─── Render polygons ─────────────────────────────────────────────────────────

  const renderPolygons = () => {
    if (!polygonPaths || !Array.isArray(polygonPaths)) return null;
    if (!mapRefToUse.current || !window.google) return null;

    return polygonPaths.map((polygon, index) => {
      const isSelected = selectedPolygon?.name === polygon.name;
      const isHighlighted = isPolygonHighlighted(polygon.name);
      const meta = getPolygonMeta(polygon.name);
      const selectedParty = meta.selectedParty || {};

      // Only the selected polygon needs its bounds (for InfoWindow placement).
      let bounds = null;
      if (isSelected) {
        bounds = new window.google.maps.LatLngBounds();
        polygon?.coordinate?.forEach((point) => bounds.extend(point));
      }

      return (
        <React.Fragment key={index}>
          <Polygon
            paths={polygon?.coordinate}
            onClick={() => handlePolygonClick(polygon)}
            onLoad={handlePolygonLoad}
            onMouseOver={(e) => {
              if (!selectedPolygon) {
                setHoveredPolygon({
                  name: polygon.name,
                  position: { lat: e.latLng.lat(), lng: e.latLng.lng() },
                });
              }
            }}
            onMouseOut={() => setHoveredPolygon(null)}
            options={{
              strokeColor: "#0D47A1",
              strokeOpacity: 0.9,
              strokeWeight: isSelected || isHighlighted ? 4 : 2,
              fillColor: getPolygonColor(polygon.name),
              fillOpacity: isSelected || isHighlighted ? 0.82 : 0.58,
              zIndex: isSelected || isHighlighted ? 1000 : 999,
            }}
          />
          {isSelected && (
            <InfoWindow
              position={bounds.getCenter()}
              onCloseClick={handleBackClick}
              options={{ maxWidth: 340 }}
            >
              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xl min-w-[280px]">
                <div className="flex items-center gap-2 mb-3">
                  <span
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: meta.color || "transparent" }}
                  />
                  <div className="text-lg font-bold text-[#123865]">
                    {selectedPolygon.name}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs text-slate-700">
                  <div className="rounded-lg bg-slate-50 p-2">
                    <div className="text-slate-500">{t("ElectionMode.winningParty")}</div>
                    <strong>{meta.winningParty || "-"}</strong>
                  </div>
                  <div className="rounded-lg bg-slate-50 p-2">
                    <div className="text-slate-500">{t("ElectionMode.winner")}</div>
                    <strong>{meta.winnerCandidate || "-"}</strong>
                  </div>
                  <div className="rounded-lg bg-slate-50 p-2">
                    <div className="text-slate-500">{t("ElectionMode.voteShare")}</div>
                    <strong>{formatPercent(meta.winnerVoteSharePercent || meta.winnerVoteShare)}</strong>
                  </div>
                  <div className="rounded-lg bg-slate-50 p-2">
                    <div className="text-slate-500">{t("ElectionMode.margin")}</div>
                    <strong>{formatPercent(meta.winnerMargin)}</strong>
                  </div>
                  <div className="rounded-lg bg-slate-50 p-2">
                    <div className="text-slate-500">{t("ElectionMode.runnerUpParty")}</div>
                    <strong>{meta.runnerUpParty || "-"}</strong>
                  </div>
                  <div className="rounded-lg bg-slate-50 p-2">
                    <div className="text-slate-500">{t("ElectionMode.runnerUp")}</div>
                    <strong>{meta.runnerUpCandidate || "-"}</strong>
                  </div>
                </div>
                <div
                  className="mt-3 rounded-lg p-2 text-xs font-semibold"
                  style={{
                    background: `${selectedParty.color || "#0b65c2"}18`,
                    color: selectedParty.color || "#0b65c2",
                    border: `1px solid ${selectedParty.color || "#0b65c2"}55`,
                  }}
                >
                  {t("ElectionMode.selectedParty")}: {selectedParty.party || "-"} · {selectedParty.category || t("ElectionMode.noCategory")}
                </div>
                <div className="mt-3 flex flex-wrap gap-2 justify-end">
                  <Button
                    size="small"
                    onClick={() =>
                      setMapAction({
                        id: Date.now(),
                        type: "note",
                        scope: "constituency",
                        constituency: selectedPolygon.name,
                      })
                    }
                  >
                    {t("ElectionMode.addNote")}
                  </Button>
                  <Button onClick={handleBackClick} size="small">
                    {t("ElectionMode.back")}
                  </Button>
                </div>
              </div>
            </InfoWindow>
          )}
        </React.Fragment>
      );
    });
  };

  // ─── Map load ────────────────────────────────────────────────────────────────

  // const onLoad = (map) => {
  //   mapRefToUse.current = map;
  //   if (polygonPaths?.length) {
  //     fitToPolygons(map, polygonPaths, center);
  //   } else {
  //     map.setCenter(center);
  //   }
  // };

  const onLoad = (map) => {
  mapRefToUse.current = map;

  if (polygonPaths?.length) {
    fitToPolygons(map, polygonPaths, center);

    window.google.maps.event.addListenerOnce(map, "idle", () => {
      if (!initialViewRef.current) {
        initialViewRef.current = {
          center: map.getCenter()?.toJSON(),
          zoom: map.getZoom(),
        };
      }
    });
  } else {
    map.setCenter(center);
  }
};

const restoreInitialView = () => {
  const map = mapRefToUse.current;

  if (!map || !initialViewRef.current) return;

  map.panTo(initialViewRef.current.center);
  map.setZoom(initialViewRef.current.zoom);
};

  if (!isLoaded) return <div>{t("ElectionMode.loadingMap")}</div>;

  return (
    <div
      className={`${
        open ? "fixed inset-0 z-[9] h-[95%] w-[98%] mx-5 my-5" : ""
      }`}
    >
      {open && (
        <ElectionModeDashboard
          electionModeId={id}
          selectedConstituencyFromMap={selectedLocationName}
          mapAction={mapAction}
          onLocationSelect={setSelectedLocationName}
          onDashboardMapChange={setDashboardMapState}
          leftPanelOpen={leftPanelOpen}
          setLeftPanelOpen={setLeftPanelOpen}
          filters={filters}
          setFilters={setFilters}
          onFilterOptionsReady={onFilterOptionsReady}
          electionModeType={electionMode?.electionModeType}
          urbanRural={electionMode?.client?.urbanRural}
          // Receives a function from the dashboard that clears all location filters
          onClearReady={(fn) => setClearDashboardFilters(() => fn)}
        />
      )}

      <GoogleMap
        mapContainerClassName={`w-full ${open ? "h-[95vh]" : "h-[500px]"}`}
        defaultCenter={center}
        defaultZoom={10}
        onLoad={onLoad}
        options={mapOptions}
      >
        {renderPolygons()}

        {/* {hoveredPolygon && hoveredPolygon.name && (() => {
          const isDark = themes === "darkMode";
          const meta = getPolygonMeta(hoveredPolygon.name);
          const accentColor = meta.color || "#3bbee8";
          return (
            <InfoWindow
              position={hoveredPolygon.position}
              options={{ disableAutoPan: true, pixelOffset: new window.google.maps.Size(0, -14) }}
            >
              <div style={{
                fontFamily: "'Segoe UI', system-ui, sans-serif",
                background: isDark ? "#1a2332" : "#ffffff",
                border: `1px solid ${isDark ? "#2d3f57" : "#e2e8f0"}`,
                borderRadius: "10px",
                padding: "0",
                minWidth: "160px",
                maxWidth: "220px",
                overflow: "hidden",
                boxShadow: isDark
                  ? "0 8px 24px rgba(0,0,0,0.5)"
                  : "0 8px 24px rgba(0,0,0,0.12)",
              }}>
                <div style={{
                  height: "4px",
                  background: accentColor,
                  borderRadius: "10px 10px 0 0",
                }} />
                <div style={{ padding: "10px 14px 12px" }}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: meta.winningParty ? "8px" : "0",
                  }}>
                    <div style={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      background: accentColor,
                      flexShrink: 0,
                    }} />
                    <span style={{
                      fontSize: "13px",
                      fontWeight: "700",
                      color: isDark ? "#e2e8f0" : "#1e293b",
                      letterSpacing: "0.2px",
                      lineHeight: "1.3",
                    }}>
                      {hoveredPolygon.name}
                    </span>
                  </div>
                  {meta.winningParty && (
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      marginTop: "6px",
                      padding: "4px 8px",
                      borderRadius: "6px",
                      background: isDark ? "#263347" : "#f1f5f9",
                    }}>
                      <span style={{
                        fontSize: "11px",
                        color: isDark ? "#94a3b8" : "#64748b",
                        fontWeight: "500",
                      }}>
                        {t("ElectionMode.winningParty")}:
                      </span>
                      <span style={{
                        fontSize: "11px",
                        fontWeight: "700",
                        color: accentColor,
                      }}>
                        {meta.winningParty}
                      </span>
                    </div>
                  )}
                  <div style={{
                    marginTop: "8px",
                    fontSize: "10px",
                    color: isDark ? "#64748b" : "#94a3b8",
                    textAlign: "center",
                    letterSpacing: "0.3px",
                  }}>
                    Click for details
                  </div>
                </div>
              </div>
            </InfoWindow>
          );
        })()} */}

        {/* District / Region InfoWindow */}
        {open &&
          (dashboardMapState.district || dashboardMapState.region) &&
          !dashboardMapState.constituency &&
          getAreaInfoPosition() && (
            <InfoWindow
              position={getAreaInfoPosition()}
              options={{ maxWidth: 300 }}
              onCloseClick={handleAreaInfoClose}
            >
              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xl">
                <div className="text-base font-bold text-[#123865]">
                  {dashboardMapState.district || dashboardMapState.region}
                </div>
                <div className="text-xs text-slate-600">
                  {dashboardMapState.selectedConstituencies.length}{" "}
                  {electionMode?.electionModeType === "assembly"
                    ? electionMode?.client?.urbanRural === "Rural"
                      ? t("ElectionMode.villagesSelected")
                      : t("ElectionMode.wardsSelected")
                    : t("ElectionMode.constituenciesSelected")}
                </div>
                {dashboardMapState.district && (
                  <div className="mt-3 flex gap-2 justify-end">
                    <Button
                      size="small"
                      onClick={() =>
                        setMapAction({
                          id: Date.now(),
                          type: "note",
                          scope: "district",
                          district: dashboardMapState.district,
                        })
                      }
                    >
                      {t("ElectionMode.addNote")}
                    </Button>
                    <Button
                      size="small"
                      onClick={() =>
                        setMapAction({
                          id: Date.now(),
                          type: "report",
                          scope: "district",
                          district: dashboardMapState.district,
                        })
                      }
                    >
                      {t("ElectionMode.viewReport")}
                    </Button>
                  </div>
                )}
              </div>
            </InfoWindow>
          )}
      </GoogleMap>

      {open && showCloseButton && (
        <Button
          onClick={() => setIsFullScreen(false)}
          type="primary"
          danger
          className="absolute top-4 right-4 z-50 bg-white text-black"
        >
          Close
        </Button>
      )}
    </div>
  );
};

export default ElectionModeMapView;
